import { aiChatSupport } from '../sessionSettings'

// Session types offered by the step-by-step creation wizard, plus the step manifest each
// one needs.
//
// This is deliberately a superset of AddSessionDialog's SESSION_TYPE_OPTIONS: that list
// has never offered Python or CPP, even though both are live, routed session types
// (Home.jsx routes 'Python'/'Vizmental' to VizmentalIDE, and TestList/createTestCode have
// had a CPP harness branch all along). The only UI that could still create them was
// CreateSessionModal, which is unreachable - imported solely by SessionMenu, which nothing
// imports. The wizard is where those types come back.
//
// `kind` drives which steps run. `testLanguage` is what TestList/createTestCode already
// derive internally from session.type - duplicated here only so the wizard can explain to
// the instructor which harness will be generated.

export const WIZARD_SESSION_TYPES = [
  // --- unit-test path: task description -> AI test cases -> generated harness ---
  {
    value: 'Python',
    label: 'Python',
    group: 'Code',
    kind: 'code',
    testLanguage: 'Python',
    blurb: 'Students write Python in the browser. Optional AI chat and test cases.',
  },
  {
    value: 'CPP',
    label: 'C++',
    group: 'Code',
    kind: 'code',
    testLanguage: 'CPP',
    blurb: 'Students write C++, compiled and run on the server. Peer chat, no AI chat.',
  },
  // Not offered as a separate choice: 'Vizmental' is how this app spells "Python with AI
  // chat on" (see sessionSettings.js). The wizard creates 'Python' and switches the type
  // when the instructor turns AI chat on in the last step. Kept here, hidden, so
  // getTypeMeta and labels still resolve for rows that already have this type.
  {
    value: 'Vizmental',
    label: 'Python (with AI chat)',
    group: 'Code',
    kind: 'code',
    testLanguage: 'Python',
    hidden: true,
    blurb: 'Python exercise with the AI chat assistant enabled.',
  },
  {
    value: 'Blockly Shared',
    label: 'Blockly Shared',
    group: 'Code',
    kind: 'code',
    testLanguage: 'Python',
    blurb: 'Block-based editing that emits Python. Peer chat, no AI chat.',
  },

  // --- SQL: no {parameter, return} test rows; the "unit test" is an AI reference query
  // authored from the Code Stubs step (StarterCode's "Generate AI Solution"). ---
  {
    value: 'SQL Schema',
    label: 'SQL Schema',
    group: 'Code',
    kind: 'sql',
    testLanguage: 'SQL',
    blurb: 'Students write a query. Graded by diffing the resulting database.',
  },

  // --- GUI/web-dev path: authored entirely in GuiExerciseEditor ---
  { value: 'HTML', label: 'GUI — HTML only', group: 'Web', kind: 'gui' },
  { value: 'CSS', label: 'GUI — CSS only', group: 'Web', kind: 'gui' },
  { value: 'JavaScript', label: 'GUI — JavaScript only', group: 'Web', kind: 'gui' },
  { value: 'Mixed', label: 'GUI — Integrated HTML/CSS/JS', group: 'Web', kind: 'gui' },
  { value: 'JSBin', label: 'GUI — JS Bin Style', group: 'Web', kind: 'gui' },
  { value: 'InitialFinal', label: 'GUI — Initial/Final Comparison', group: 'Web', kind: 'gui' },
  { value: 'InitialFinalCSS', label: 'GUI — Initial/Final Comparison (CSS)', group: 'Web', kind: 'gui' },
  { value: 'InitialFinalJS', label: 'GUI — Initial/Final Comparison (JavaScript)', group: 'Web', kind: 'gui' },

  // --- quiz ---
  { value: 'Pop Quiz', label: 'Pop Quiz', group: 'Other', kind: 'quiz' },
]

export const getTypeMeta = (type) =>
  WIZARD_SESSION_TYPES.find((t) => t.value === type) || null

// Step ids are also the rail labels. `persists` marks steps whose child component writes
// straight to the DB on its own schedule (blur-save, explicit Save button), which is why
// the wizard refetches the session when leaving them.
const DETAILS = { id: 'details', label: 'Problem Details' }
const TYPE = { id: 'type', label: 'Language' }
const TESTS = { id: 'tests', label: 'Testcases', persists: true }
const STUBS = { id: 'stubs', label: 'Code Stubs', persists: true }
const GUI = { id: 'gui', label: 'Exercise & Checks', persists: true }
const QUIZ = { id: 'quiz', label: 'Questions', persists: true }
// Last step for every type that has a student AI chat. The wizard writes it itself on
// Finish (it is the only step whose settings are not owned by an embedded panel).
const AI = { id: 'ai', label: 'AI Chat' }
// Only while the header's Experiment switch is on, as the last step. The steps before it
// author what every condition shares; AI chat can differ between conditions, so it moves
// into this step (one set of chat settings per condition) and the AI Chat step is left out.
// Held in wizard state until Publish.
const EXPERIMENT = { id: 'experiment', label: 'Experiment' }

// Testcases deliberately precedes Code Stubs, which is the reverse of HackerRank's order.
// It has to: TestList's "Create Unit Tests" calls createTestCode, which returns
// `template: ''` (testService.js:349), and then writes the WHOLE test_code object
// (TestList.jsx:128 and :180). Running it after the instructor has authored the student
// code template silently blanks that template. Generating the harness first, then editing
// stubs, is the only ordering that doesn't lose work.
const STEPS_BY_KIND = {
  code: [DETAILS, TYPE, TESTS, STUBS],
  sql: [DETAILS, TYPE, STUBS],
  gui: [DETAILS, TYPE, GUI],
  quiz: [DETAILS, TYPE, QUIZ],
}

// Only the code kind has a test-case toggle: SQL is graded against a reference query,
// GUI types against their check list, and neither goes through TestList. The toggle lives
// on the Testcases step itself, which is always shown for code types - turning tests off
// there just means there is nothing else to configure on it.
export const supportsTestsToggle = (type) => getTypeMeta(type)?.kind === 'code'

// Before a type is picked the rail still needs something to show. Fall back to the code
// flow, which is the longest, so the rail shows its full shape up front and only ever
// shrinks once a type narrows it - rather than growing from two entries and shifting the
// layout under the instructor mid-step.
//
// The AI Chat step is appended only where students can actually get AI chat. In
// experiment mode the Experiment step is appended instead (it holds each condition's chat).
export const getStepsForType = (type, { experiment = false } = {}) => {
  const meta = getTypeMeta(type)
  const kindSteps = meta ? STEPS_BY_KIND[meta.kind] || STEPS_BY_KIND.code : STEPS_BY_KIND.code
  if (experiment) return [...kindSteps, EXPERIMENT]
  return !meta || aiChatSupport(type) !== 'unavailable' ? [...kindSteps, AI] : kindSteps
}
